import {
  DeleteFilterSuccess,
  FetchFilterSearchCriteriaSuccess,
  FilterActions,
  FilterActionTypes,
  SelectFilter
} from '@app/pcs/actions/filter';
import { FilterEditActionTypes } from '@app/pcs/actions/filter-edit';
import { LabelActionTypes } from '@app/pcs/actions/label';
import { RecentActivityActionTypes } from '@app/pcs/actions/recent-activity';
import {
  FetchUserFiltersSuccess,
  SaveUserFilterSuccess,
  UpdateUserFilterSuccess,
  UserFilterActions,
  UserFilterActionTypes
} from '@app/pcs/actions/user-filter';
import { QuickSearchActionTypes } from '@app/pcs/dashboard/actions/quick-search';
import { DossierSearchKey, Filter, UserFilter } from '@app/pcs/models';
import { isDossierNavigationAction } from '@app/pcs/util';
import { getFilterName, getTtpId } from '@app/pcs/util/user-filter';
import { BackButtonActionTypes } from '@dossier/actions/back-button';
import { Action } from '@ngrx/store';
import { createHandler, createReducer, persistReducer } from '@util';
import * as R from 'ramda';

interface LocalStorageState {
  selectedFilterId: number;
  selectedFilterName: string;
  ttpId: number;
}

export interface State {
  filters: UserFilter[];
  selectedFilterId: number;
  selectedFilterName: string;
  ttpId: number;
  displayFilterForm: boolean;
}

export const initialState: State = {
  filters: [],
  selectedFilterId: null,
  selectedFilterName: null,
  ttpId: null,
  displayFilterForm: false
};

// Selectors
type GetFilters = (s: State) => UserFilter[];
export const getFilters: GetFilters = R.prop('filters');

type GetSelectedFilterId = (s: State) => number;
export const getSelectedFilterId: GetSelectedFilterId = R.prop('selectedFilterId');

type GetSelectedFilterName = (s: State) => string;
export const getSelectedFilterName: GetSelectedFilterName = R.prop('selectedFilterName');

type GetSelectedFilterTtp = (s: State) => number;
export const getSelectedFilterTtp: GetSelectedFilterTtp = R.prop('ttpId');

type GetDisplayFilterForm = (s: State) => boolean;
export const getDisplayFilterForm: GetDisplayFilterForm = R.prop('displayFilterForm');

// Handlers
const filterIdEq = R.pathEq(['filter', 'id']);

const findFilterById = (filterId: number, filters: UserFilter[]): UserFilter =>
  R.find(filterIdEq(filterId), filters);

const getSelectedFilterState = (selectedFilter: UserFilter): Partial<State> => selectedFilter
  ? ({
    selectedFilterName: getFilterName(selectedFilter),
    ttpId: getTtpId(selectedFilter)
  })
  : {};

const fetchFiltersSuccess = (state: State, {payload: filters}: FetchUserFiltersSuccess): State => {
  const {selectedFilterId} = state;
  const selectedFilter = selectedFilterId && findFilterById(selectedFilterId, filters);

  return R.mergeRight(state, {
    filters,
    ...getSelectedFilterState(selectedFilter)
  }) as State;
};

const selectFilter = (state: State, {payload: selectedFilterId}: SelectFilter): State => {
  const {filters} = state;
  const selectedFilter = findFilterById(selectedFilterId, filters);

  return R.mergeDeepRight(state, {
    selectedFilterId,
    ...getSelectedFilterState(selectedFilter)
  }) as State;
};

const deselectFilter = (state: State): State =>
  R.mergeRight(state, {
    selectedFilterId: null,
    selectedFilterName: null,
    ttpId: null
  });

const toggleFilterForm = (state: State): State =>
  R.evolve({
    displayFilterForm: R.not
  }, state);

const closeFilterForm = (state: State): State =>
  R.mergeRight(state, {
    displayFilterForm: false
  });

type AddFilterToFilters = (userFilter: UserFilter) => (userFilters: UserFilter[]) => UserFilter[];
const addFilterToFilters = <AddFilterToFilters>R.curry((userFilter: UserFilter, userFilters: UserFilter[]) => {
  const indexOfExistingFilter = R.findIndex(filterIdEq(userFilter.filter.id), userFilters);
  return indexOfExistingFilter !== -1 ? R.update(indexOfExistingFilter, userFilter, userFilters) : R.append(userFilter, userFilters);
});

const addFilter = (state: State, {payload: filter}: SaveUserFilterSuccess): State =>
  R.evolve({
    filters: addFilterToFilters(filter)
  }, state);

const updateUserFilter = (state: State, {payload: updatedUserFilter}: UpdateUserFilterSuccess): State => {
  const {filters} = state;
  const userFilterIndex = R.findIndex(filterIdEq(updatedUserFilter.filter.id), filters);

  return userFilterIndex >= 0
    ? R.mergeRight(state, {
      filters: R.update(userFilterIndex, updatedUserFilter, filters) as UserFilter[]
    }) as State
    : state;
};

const addSearchCriteriaToFilter = (state: State, {payload: filter}: FetchFilterSearchCriteriaSuccess): State => {
  const {id, dossierSearchModel} = filter;
  const filterIndex = R.findIndex(filterIdEq(id), state.filters);
  const updatedFilter = R.evolve({
    filter: (currentFilter: Filter) => R.mergeRight(currentFilter, {
      dossierSearchModel
    })
  }, <any>state.filters[filterIndex]);
  return R.evolve({
    filters: R.update(filterIndex, updatedFilter)
  }, state);
};

const deleteFilter = (state: State, {payload: filterId}: DeleteFilterSuccess): State => {
  const filterIndex = R.findIndex(filterIdEq(filterId), state.filters);
  return R.evolve({
    filters: R.remove(filterIndex, 1)
  }, state);
};

const fetchFiltersSuccessHandler = createHandler(UserFilterActionTypes.FetchUserFiltersSuccess, fetchFiltersSuccess);
const selectFilterHandler = createHandler(FilterActionTypes.SelectFilter, selectFilter);
const deselectHandler = createHandler([
  LabelActionTypes.SelectLabel,
  RecentActivityActionTypes.SelectRecentActivity,
  QuickSearchActionTypes.SetSearchTerm
],
deselectFilter
);
const toggleFilterFormHandler = createHandler(FilterActionTypes.ToggleFilterForm, toggleFilterForm);
const closeFilterFormHandler = createHandler(FilterEditActionTypes.ResetForm, closeFilterForm);
const addFilterHandler = createHandler(UserFilterActionTypes.SaveUserFilterSuccess, addFilter);
const updateUserFilterHandler = createHandler(UserFilterActionTypes.UpdateUserFilterSuccess, updateUserFilter);
const addSearchCriteriaToFilterHandler = createHandler(FilterActionTypes.FetchFilterSearchCriteriaSuccess, addSearchCriteriaToFilter);
const deleteFilterHandler = createHandler(FilterActionTypes.DeleteFilterSuccess, deleteFilter);

const filterReducer = createReducer<State, FilterActions | UserFilterActions>([
  fetchFiltersSuccessHandler,
  selectFilterHandler,
  deselectHandler,
  toggleFilterFormHandler,
  closeFilterFormHandler,
  addFilterHandler,
  updateUserFilterHandler,
  addSearchCriteriaToFilterHandler,
  deleteFilterHandler
], initialState);

export const reducer = persistReducer<State, LocalStorageState>(
  {
    storageKey: DossierSearchKey.Filters,
    shouldSync: (_, action: Action): boolean => isDossierNavigationAction(action),
    shouldReinitialize: (_, action: Action): boolean => action.type === BackButtonActionTypes.GoBack,
    mergeStates: (state: State, {selectedFilterId, selectedFilterName, ttpId}: LocalStorageState): State =>
      R.mergeRight(state, {selectedFilterId, selectedFilterName, ttpId}),
    mapper: ({selectedFilterId, filters}: State) => {
      const selectedFilter = selectedFilterId && findFilterById(selectedFilterId, filters);
      return {
        selectedFilterId,
        ...getSelectedFilterState(selectedFilter)
      } as LocalStorageState;
    }
  },
  filterReducer
);
